import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { IndianRupee, Receipt, RotateCcw, Undo2 } from 'lucide-react';
import type { OrderWithItems, Payment } from '@tailoring-crm/shared';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';
import { useAuth } from '../../lib/auth/auth-context.js';
import { orderPaymentsPath } from '../../lib/payments/payments-api.js';
import { useOrderPayments, useReversePayment } from '../../lib/payments/use-payments.js';
import { formatCurrency, formatDate } from '../../lib/format.js';
import { RecordPaymentDialog } from './record-payment-dialog.js';
import { ReceiptDialog } from './receipt-dialog.js';

/**
 * The money on one order, on the screen the counter already has open.
 *
 * The balance is the loudest thing here, because it is the number the shop is looking for when
 * a customer walks in to collect. Everything else is the trail underneath it: what was taken,
 * when, how, by whom — including the entries that were reversed, which stay visible with the
 * reason written across them. Hiding a reversed entry would make the ledger read as though the
 * mistake never happened, which is the one thing this module exists to prevent.
 *
 * A tailor sees none of it. The API refuses them every payment route; this returns null so
 * they are not shown a card that would only ever answer 403.
 */
export function PaymentsCard({ order }: { order: OrderWithItems }): ReactElement | null {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();

  const payments = useOrderPayments(role === 'tailor' ? null : order.id);
  const reverse = useReversePayment(order.id);

  const [collecting, setCollecting] = useState<'payment' | 'refund' | null>(null);
  const [receipt, setReceipt] = useState<Payment | null>(null);
  const [reversing, setReversing] = useState<Payment | null>(null);
  const [reason, setReason] = useState('');

  if (role === 'tailor') return null;

  const view = payments.data;
  const outstandingPaise = view?.outstandingPaise ?? Math.max(0, order.balancePaise);
  const creditPaise = view?.creditPaise ?? 0;
  const paidPaise = view?.paidPaise ?? order.amountPaidPaise;

  const onReverse = async (): Promise<void> => {
    if (!reversing) return;
    try {
      await reverse.mutateAsync({ paymentId: reversing.id, reason: reason.trim() });
      toast({ title: t('payments.reversed'), variant: 'success' });
      setReversing(null);
      setReason('');
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">{t('payments.heading')}</h3>
          <p className="text-sm text-muted-foreground">
            {creditPaise > 0
              ? t('payments.creditHeld', { amount: formatCurrency(creditPaise) })
              : outstandingPaise > 0
                ? t('payments.outstandingIs', { amount: formatCurrency(outstandingPaise) })
                : t('payments.fullySettled')}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {!order.cancelledAt ? (
            <Button size="sm" onClick={() => setCollecting('payment')}>
              <IndianRupee aria-hidden="true" />
              {t('payments.record')}
            </Button>
          ) : null}
          {paidPaise > 0 ? (
            <Button variant="outline" size="sm" onClick={() => setCollecting('refund')}>
              <Undo2 aria-hidden="true" />
              {t('payments.refund')}
            </Button>
          ) : null}
        </div>
      </div>

      <dl className="grid grid-cols-3 gap-3 text-sm">
        <div className="rounded-md bg-muted/50 p-2">
          <dt className="text-xs text-muted-foreground">{t('orders.total')}</dt>
          <dd className="font-semibold tabular-nums">{formatCurrency(order.totalPaise)}</dd>
        </div>
        <div className="rounded-md bg-muted/50 p-2">
          <dt className="text-xs text-muted-foreground">{t('orders.paid')}</dt>
          <dd className="font-semibold tabular-nums">{formatCurrency(paidPaise)}</dd>
        </div>
        <div className="rounded-md bg-muted/50 p-2">
          <dt className="text-xs text-muted-foreground">{t('orders.balance')}</dt>
          <dd className="font-semibold tabular-nums">
            {outstandingPaise > 0 ? formatCurrency(outstandingPaise) : t('orders.settled')}
          </dd>
        </div>
      </dl>

      {view && view.entries.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {view.entries.map((entry) => {
            const reversed = entry.reversedByPaymentId !== null;
            const isReversal = entry.kind === 'reversal';
            return (
              <li
                key={entry.id}
                className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-border p-2 text-sm"
              >
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    <span className={reversed ? 'line-through' : undefined}>
                      {entry.signedAmountPaise < 0 ? '−' : ''}
                      {formatCurrency(entry.amountPaise)}
                    </span>
                    <span className="text-muted-foreground">
                      {t(`payments.methods.${entry.method}`)}
                    </span>
                    {isReversal ? <Badge variant="warning">{t('payments.reversal')}</Badge> : null}
                    {entry.kind === 'refund' ? (
                      <Badge variant="secondary">{t('payments.refundBadge')}</Badge>
                    ) : null}
                    {reversed ? (
                      <Badge variant="warning">{t('payments.reversedBadge')}</Badge>
                    ) : null}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(entry.receivedAt)}
                    {entry.recordedByName ? ` · ${entry.recordedByName}` : ''}
                    {entry.reference ? ` · ${entry.reference}` : ''}
                  </p>
                  {entry.note ? (
                    <p className="text-xs text-muted-foreground">{entry.note}</p>
                  ) : null}
                  {entry.reason ? (
                    <p className="text-xs text-muted-foreground">
                      {t('payments.reversalReason', { reason: entry.reason })}
                    </p>
                  ) : null}
                </div>

                <div className="flex shrink-0 gap-1">
                  {!isReversal ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('payments.receiptFor', {
                        amount: formatCurrency(entry.amountPaise),
                      })}
                      onClick={() => setReceipt(entry)}
                    >
                      <Receipt aria-hidden="true" />
                    </Button>
                  ) : null}
                  {/* Undoing an entry is the owner's alone — the API says so too, and this
                      only spares a manager a button that would 403. */}
                  {!isReversal && !reversed && role === 'owner' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('payments.reverseFor', {
                        amount: formatCurrency(entry.amountPaise),
                      })}
                      onClick={() => setReversing(entry)}
                    >
                      <RotateCcw aria-hidden="true" />
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{t('payments.noneYet')}</p>
      )}

      {collecting ? (
        <RecordPaymentDialog
          paymentsPath={orderPaymentsPath(order.id)}
          number={order.orderNumber}
          kind={collecting}
          totalPaise={order.totalPaise}
          paidPaise={paidPaise}
          outstandingPaise={outstandingPaise}
          suggestedMethod={view?.suggestedMethod ?? 'cash'}
          onClose={() => setCollecting(null)}
          onRecorded={(payment) => setReceipt(payment)}
        />
      ) : null}

      {receipt && view ? (
        <ReceiptDialog
          payment={receipt}
          // The freshest ledger, so a receipt opened straight after recording shows the entry
          // that was just written rather than the view it was taken from.
          payments={view}
          onClose={() => setReceipt(null)}
        />
      ) : null}

      {/* A reversal without a reason is the argument this field exists to end. */}
      <Dialog open={reversing !== null} onOpenChange={(open) => !open && setReversing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('payments.reverseTitle', {
                amount: formatCurrency(reversing?.amountPaise ?? 0),
              })}
            </DialogTitle>
            <DialogDescription>{t('payments.reverseHint')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reversal-reason">{t('payments.reverseReasonLabel')}</Label>
            <Input
              id="reversal-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReversing(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={reason.trim().length < 3 || reverse.isPending}
              onClick={() => void onReverse()}
            >
              {t('payments.reverseConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default PaymentsCard;
